import React, { useState, useEffect } from 'react';
import { 
    Music, 
    Plus, 
    Edit3, 
    Trash2, 
    Eye,
    EyeOff,
    Calendar,
    ArrowUp,
    ArrowDown,
    Search,
    Filter,
    Play,
    Pause
} from 'lucide-react';
import api from '../services/api';
import trackingService from '../services/tracking';

const FeaturedSongsManagement = () => {
    const [featuredSongs, setFeaturedSongs] = useState([]);
    const [availableUploads, setAvailableUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedSong, setSelectedSong] = useState(null);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [audio] = useState(new Audio());

    const [formData, setFormData] = useState({
        uploadId: '',
        displayOrder: 0,
        isActive: true,
        featuredUntil: ''
    });

    useEffect(() => {
        fetchFeaturedSongs();
        fetchAvailableUploads();
    }, []);

    useEffect(() => {
        // Cleanup audio when component unmounts
        return () => {
            audio.pause();
            audio.src = '';
            trackingService.cleanup();
        };
    }, [audio]);

    const fetchFeaturedSongs = async () => {
        try {
            setLoading(true);
            const response = await api.get('/featured-songs?include_inactive=true');
            if (response.data.success) {
                setFeaturedSongs(response.data.data);
            } else {
                setError('Failed to load featured songs');
            }
        } catch (err) {
            console.error('Error fetching featured songs:', err);
            setError('Unable to load featured songs. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableUploads = async () => {
        try {
            // Get all audio uploads
            const response = await api.get('/upload?type=audio&limit=1000');
            if (response.data.success) {
                setAvailableUploads(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching available uploads:', err);
        }
    };

    const handleCreateFeaturedSong = async () => {
        try {
            setLoading(true);
            const response = await api.post('/featured-songs', formData);
            
            if (response.data.success) {
                setShowAddModal(false);
                resetForm();
                // Refresh the entire list instead of just adding to it
                await fetchFeaturedSongs();
            } else {
                setError(response.data.message || 'Failed to create featured song');
            }
        } catch (err) {
            console.error('Error creating featured song:', err);
            setError(err.response?.data?.message || 'Failed to create featured song');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateFeaturedSong = async () => {
        try {
            setLoading(true);
            const response = await api.put(`/featured-songs/${selectedSong.id}`, formData);
            
            if (response.data.success) {
                setFeaturedSongs(featuredSongs.map(song => 
                    song.id === selectedSong.id ? response.data.data : song
                ));
                setShowEditModal(false);
                setSelectedSong(null);
                resetForm();
            } else {
                setError(response.data.message || 'Failed to update featured song');
            }
        } catch (err) {
            console.error('Error updating featured song:', err);
            setError(err.response?.data?.message || 'Failed to update featured song');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFeaturedSong = async (songId) => {
        if (!confirm('Are you sure you want to remove this song from featured?')) return;
        
        try {
            const response = await api.delete(`/featured-songs/${songId}`);
            
            if (response.data.success) {
                setFeaturedSongs(featuredSongs.filter(song => song.id !== songId));
            } else {
                setError(response.data.message || 'Failed to delete featured song');
            }
        } catch (err) {
            console.error('Error deleting featured song:', err);
            setError('Failed to delete featured song');
        }
    };

    const handleReorder = async (songId, direction) => {
        const currentIndex = featuredSongs.findIndex(song => song.id === songId);
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        if (newIndex < 0 || newIndex >= featuredSongs.length) return;

        const reorderedSongs = [...featuredSongs];
        const [moved] = reorderedSongs.splice(currentIndex, 1);
        reorderedSongs.splice(newIndex, 0, moved);

        try {
            const orderedIds = reorderedSongs.map(song => song.id);
            await api.put('/featured-songs/reorder', { orderedIds });
            setFeaturedSongs(reorderedSongs);
        } catch (err) {
            console.error('Error reordering featured songs:', err);
            setError('Failed to reorder featured songs');
            // Revert on error
            fetchFeaturedSongs();
        }
    };

    const togglePlayback = async (song) => {
        if (currentlyPlaying === song.upload_id) {
            audio.pause();
            setCurrentlyPlaying(null);
            trackingService.stopPlayTracking(song.upload_id);
        } else {
            try {
                if (currentlyPlaying) {
                    audio.pause();
                    trackingService.stopPlayTracking(currentlyPlaying);
                }
                
                audio.src = song.file_url;
                await audio.play();
                setCurrentlyPlaying(song.upload_id);
                
                // Start tracking with the tracking service
                trackingService.startPlayTracking(song.upload_id, audio);
                
                audio.onended = () => {
                    setCurrentlyPlaying(null);
                };
                audio.onerror = () => {
                    setCurrentlyPlaying(null);
                    setError('Failed to play audio file');
                };
            } catch (err) {
                console.error('Error playing audio:', err);
                setError('Failed to play audio file');
            }
        }
    };

    const openEditModal = (song) => {
        setSelectedSong(song);
        setFormData({
            uploadId: song.upload_id,
            displayOrder: song.display_order,
            isActive: song.is_active,
            featuredUntil: song.featured_until ? song.featured_until.substring(0, 16) : ''
        });
        setShowEditModal(true);
    };

    const resetForm = () => {
        setFormData({
            uploadId: '',
            displayOrder: 0,
            isActive: true,
            featuredUntil: ''
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No expiry';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '--:--';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const filteredSongs = featuredSongs.filter(song =>
        song.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        song.artist?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        song.artist_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading && featuredSongs.length === 0) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Music className="w-12 h-12 text-yellow-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading featured songs...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-black gradient-text mb-2">
                            Featured Songs Management
                        </h1>
                        <p className="text-gray-400">
                            Manage songs displayed on the homepage and dashboard
                        </p>
                    </div>
                    
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-black font-medium rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Add Featured Song
                    </button>
                </div>

                {/* Search and Filters */}
                <div className="glass-card p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search songs, artists..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-lg mb-6">
                        {error}
                        <button
                            onClick={() => setError(null)}
                            className="float-right text-red-500 hover:text-red-300"
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Featured Songs List */}
                <div className="space-y-4">
                    {filteredSongs.length === 0 ? (
                        <div className="glass-card p-8 text-center">
                            <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-300 mb-2">
                                No Featured Songs
                            </h3>
                            <p className="text-gray-400 mb-4">
                                {searchTerm ? 'No songs match your search.' : 'Start by adding some featured songs.'}
                            </p>
                        </div>
                    ) : (
                        filteredSongs.map((song, index) => (
                            <div key={song.id} className="glass-card p-6 hover:border-yellow-500/30 transition-all">
                                <div className="flex items-center gap-4">
                                    {/* Cover Art / Play Button */}
                                    <div className="relative">
                                        {song.cover_art_url ? (
                                            <img
                                                src={song.cover_art_url}
                                                alt={song.title}
                                                width="64"
                                                height="64"
                                                className="w-16 h-16 rounded-lg object-cover"
                                            />
                                        ) : (
                                            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                                                <Music className="w-8 h-8 text-white" />
                                            </div>
                                        )}
                                        
                                        <button
                                            onClick={() => togglePlayback(song)}
                                            className="absolute inset-0 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center transition-all opacity-0 hover:opacity-100"
                                        >
                                            {currentlyPlaying === song.upload_id ? (
                                                <Pause className="w-6 h-6 text-white" />
                                            ) : (
                                                <Play className="w-6 h-6 text-white" />
                                            )}
                                        </button>
                                    </div>

                                    {/* Song Info */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-semibold text-white truncate">
                                            {song.title}
                                        </h3>
                                        <p className="text-gray-400 text-sm">
                                            by {song.artist_name || song.artist}
                                        </p>
                                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                            <span>{song.genre}</span>
                                            <span>{formatDuration(song.duration)}</span>
                                            <span>Order: {song.display_order}</span>
                                            {song.featured_until && (
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(song.featured_until)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                            song.is_active && (!song.featured_until || new Date(song.featured_until) > new Date())
                                                ? 'bg-green-500/20 text-green-400'
                                                : 'bg-gray-500/20 text-gray-400'
                                        }`}>
                                            {song.is_active && (!song.featured_until || new Date(song.featured_until) > new Date())
                                                ? 'Active'
                                                : 'Inactive'
                                            }
                                        </span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        {/* Reorder buttons */}
                                        <div className="flex flex-col gap-1">
                                            <button
                                                onClick={() => handleReorder(song.id, 'up')}
                                                disabled={index === 0}
                                                className={`p-1 rounded transition-colors ${
                                                    index === 0
                                                        ? 'text-gray-600 cursor-not-allowed'
                                                        : 'text-gray-400 hover:text-yellow-400'
                                                }`}
                                            >
                                                <ArrowUp className="w-3 h-3" />
                                            </button>
                                            <button
                                                onClick={() => handleReorder(song.id, 'down')}
                                                disabled={index === filteredSongs.length - 1}
                                                className={`p-1 rounded transition-colors ${
                                                    index === filteredSongs.length - 1
                                                        ? 'text-gray-600 cursor-not-allowed'
                                                        : 'text-gray-400 hover:text-yellow-400'
                                                }`}
                                            >
                                                <ArrowDown className="w-3 h-3" />
                                            </button>
                                        </div>

                                        <button
                                            onClick={() => openEditModal(song)}
                                            className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
                                        >
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        
                                        <button
                                            onClick={() => handleDeleteFeaturedSong(song.id)}
                                            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Add Featured Song Modal */}
                {showAddModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold text-white mb-4">Add Featured Song</h2>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Select Song
                                    </label>
                                    <select
                                        value={formData.uploadId}
                                        onChange={(e) => setFormData({...formData, uploadId: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-yellow-500"
                                    >
                                        <option value="">Select a song...</option>
                                        {availableUploads.map(upload => (
                                            <option key={upload.id} value={upload.id}>
                                                {upload.title} - {upload.artist}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Display Order
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.displayOrder}
                                        onChange={(e) => setFormData({...formData, displayOrder: parseInt(e.target.value)})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-yellow-500"
                                        min="0"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Featured Until (Optional)
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.featuredUntil}
                                        onChange={(e) => setFormData({...formData, featuredUntil: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-yellow-500"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                                        className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                                    />
                                    <label htmlFor="isActive" className="text-gray-300 text-sm">
                                        Active
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowAddModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateFeaturedSong}
                                    disabled={!formData.uploadId}
                                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-medium rounded-lg transition-colors"
                                >
                                    Add Song
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Featured Song Modal */}
                {showEditModal && selectedSong && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold text-white mb-4">Edit Featured Song</h2>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Song
                                    </label>
                                    <div className="text-white font-medium">
                                        {selectedSong.title} - {selectedSong.artist}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Display Order
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.displayOrder}
                                        onChange={(e) => setFormData({...formData, displayOrder: parseInt(e.target.value)})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-yellow-500"
                                        min="0"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Featured Until (Optional)
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.featuredUntil}
                                        onChange={(e) => setFormData({...formData, featuredUntil: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-yellow-500"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="editIsActive"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                                        className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                                    />
                                    <label htmlFor="editIsActive" className="text-gray-300 text-sm">
                                        Active
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedSong(null);
                                        resetForm();
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateFeaturedSong}
                                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-black font-medium rounded-lg transition-colors"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeaturedSongsManagement;